import { createReactBlockSpec, useEditorState } from "@blocknote/react"
import { lazy, Suspense, useContext, useState } from "react"
import { toast } from "sonner"
import { LoadingState } from "~/components/screens/StateScreen"
import { Button } from "~/components/ui/button"
import { FormFillContext } from "~/components/forms/question-block"
import { FormPagination } from "~/components/forms/FormPagination"
import { useFormPagination } from "~/hooks/useFormPagination"
import { SearchableSelect } from "~/components/ui/searchable-select"
import { formEmbedBlockConfig } from "~/lib/editor-content"
import {
	formAnswersWithDefaults,
	formQuestionsFromContent,
	validateFormAnswers
} from "~/lib/forms"
import { TeamPermissions } from "~/lib/permissions"
import { usePermission } from "~/hooks/usePermission"
import { useTeam } from "~/hooks/useTeam"
import { api, type RouterInputs } from "~/trpc/react"

const EmbeddedFormEditor = lazy(() =>
	import("./Editor").then(({ Editor }) => ({ default: Editor }))
)

/** Embeds one of the team's forms inside a document; readers fill it in place. */
export const FormEmbed = createReactBlockSpec(formEmbedBlockConfig, {
	render: function FormEmbedBlock({ block, editor }) {
		const editable = useEditorState({
			editor,
			selector: ({ editor }) => editor.isEditable
		})
		const containingForm = useContext(FormFillContext)
		const { team, isLoading } = useTeam()
		const { hasTeamPermission } = usePermission({ activeTeamId: team?.id })
		const canView = hasTeamPermission(TeamPermissions.VIEW_FORMS)
		const formId = block.props.formId
		const formQuery = api.forms.get.useQuery(
			{ teamId: team?.id ?? "", formId },
			{
				enabled: !containingForm && Boolean(team?.id && formId) && canView,
				retry: false
			}
		)
		const forms = api.forms.list.useQuery(
			{ teamId: team?.id ?? "" },
			{ enabled: !containingForm && editable && Boolean(team?.id) && canView }
		)
		const [answers, setAnswers] = useState<
			{ fieldId: string; value: unknown }[]
		>([])
		const submit = api.forms.submit.useMutation({
			onSuccess: () => {
				toast.success("Submission saved")
				setAnswers([])
				pagination.setPage(0)
			},
			onError: (error) =>
				toast.error(error.message || "Could not submit the form")
		})
		const uploadMutation = api.files.uploadTeamFile.useMutation()
		const [errors, setErrors] = useState<Record<string, string>>({})
		const pagination = useFormPagination(
			formQuery.data?.content,
			setErrors,
			answers
		)
		if (containingForm)
			return (
				<p contentEditable={false} className="text-sm text-text-sub">
					Nested forms are not supported.
				</p>
			)
		if (isLoading) return <LoadingState />

		if (!canView)
			return (
				<div contentEditable={false} className="flex w-full items-center gap-2">
					<p className="text-sm text-text-sub">
						Embedded Orbit form (no access)
					</p>
				</div>
			)

		if (
			!formId ||
			(editable && hasTeamPermission(TeamPermissions.EDIT_FORMS))
		) {
			return (
				<div contentEditable={false} className="flex w-full flex-col gap-2">
					<SearchableSelect
						value={formId || ""}
						onChange={(next) => {
							if (!next || next === formId) return
							editor.updateBlock(block, { props: { formId: next } })
						}}
						placeholder="Choose a form to embed"
						options={(forms.data ?? []).map((form) => ({
							value: form.id,
							label: form.name
						}))}
					/>
					{formId && (
						<Button
							variant="outline"
							className="self-start"
							onClick={() =>
								editor.updateBlock(block, { props: { formId: "" } })
							}
						>
							Clear embed
						</Button>
					)}
				</div>
			)
		}

		if (formQuery.isLoading) return <LoadingState />
		if (formQuery.error || !formQuery.data)
			return (
				<div contentEditable={false} className="flex w-full items-center gap-2">
					<p className="text-sm text-text-sub">
						Embedded form unavailable. It may live in another team or was
						deleted.
					</p>
				</div>
			)
		const data = formQuery.data
		const questions = formQuestionsFromContent(data.content)
		const fillState = {
			filling: true,
			pagination,
			upload: async (input: { name: string; type: string; data: string }) =>
				uploadMutation.mutateAsync({ teamId: team?.id ?? "", ...input }),
			fields: questions,
			errors,
			answers: formAnswersWithDefaults(questions, answers),
			setAnswer: (fieldId: string, value: unknown) =>
				setAnswers((current) => [
					...current.filter((answer) => answer.fieldId !== fieldId),
					{ fieldId, value }
				])
		}
		const submitForm = () => {
			if (!team) return
			const result = validateFormAnswers(questions, answers)
			setErrors(result.errors)
			if (!result.ok) {
				toast.error(
					result.errors.form || "Check the highlighted questions and try again."
				)
				return
			}
			submit.mutate({
				teamId: team.id,
				formId: data.id,
				answers: result.answers as RouterInputs["forms"]["submit"]["answers"]
			})
		}
		return (
			<div
				contentEditable={false}
				className="w-full max-w-3xl rounded-lg border border-stroke p-4"
			>
				<h4 className="mb-3 text-base font-semibold text-text-main">
					{data.name}
				</h4>
				<FormFillContext.Provider value={fillState}>
					<Suspense fallback={<LoadingState />}>
						<EmbeddedFormEditor
							key={data.id}
							variant="form"
							teamId={team?.id ?? ""}
							initialState={data.content}
							disabled
						/>
					</Suspense>
					<div className="mt-3">
						<FormPagination
							pending={submit.isPending}
							disabled={uploadMutation.isPending}
							onSubmit={submitForm}
						/>
					</div>
				</FormFillContext.Provider>
			</div>
		)
	},
	toExternalHTML: ({ block }) =>
		block.props.formId ? <p>[Orbit form]</p> : <p />
})
